/**
 * Obstetric Ultrasound Calculations
 * Industry-standard formulas for fetal biometry and growth assessment
 *
 * All measurements in millimeters (mm), weights in grams (g)
 * See /docs/OB_CALCULATION_REFERENCES.md for formula sources
 */

// ============================================
// ESTIMATED FETAL WEIGHT (EFW)
// ============================================

/**
 * Hadlock I - Using BPD and AC
 * Log10(EFW) = 1.1134 + 0.05845(AC) - 0.000604(AC²) - 0.007365(BPD²) + 0.000595(BPD)(AC) + 0.1694(BPD)
 */
export const hadlockEFW_BPD_AC = (bpd, ac) => {
  if (!bpd || !ac) return null;
  const bpdCm = bpd / 10;
  const acCm = ac / 10;

  const log10EFW = 1.1134 +
    (0.05845 * acCm) -
    (0.000604 * Math.pow(acCm, 2)) -
    (0.007365 * Math.pow(bpdCm, 2)) +
    (0.000595 * bpdCm * acCm) +
    (0.1694 * bpdCm);

  return Math.round(Math.pow(10, log10EFW));
};

/**
 * Hadlock II - Using AC and FL
 * Log10(EFW) = 1.304 + 0.05281(AC) + 0.1938(FL) - 0.004(AC)(FL)
 */
export const hadlockEFW_AC_FL = (ac, fl) => {
  if (!ac || !fl) return null;
  const acCm = ac / 10;
  const flCm = fl / 10;

  const log10EFW = 1.304 +
    (0.05281 * acCm) +
    (0.1938 * flCm) -
    (0.004 * acCm * flCm);

  return Math.round(Math.pow(10, log10EFW));
};

/**
 * Hadlock III - Using BPD, AC, and FL (Most commonly used)
 * Log10(EFW) = 1.335 - 0.0034(AC)(FL) + 0.0316(BPD) + 0.0457(AC) + 0.1623(FL)
 */
export const hadlockEFW_BPD_AC_FL = (bpd, ac, fl) => {
  if (!bpd || !ac || !fl) return null;
  const bpdCm = bpd / 10;
  const acCm = ac / 10;
  const flCm = fl / 10;

  const log10EFW = 1.335 -
    (0.0034 * acCm * flCm) +
    (0.0316 * bpdCm) +
    (0.0457 * acCm) +
    (0.1623 * flCm);

  return Math.round(Math.pow(10, log10EFW));
};

/**
 * Hadlock IV - Using HC, AC, and FL (Alternative when BPD unreliable)
 * Log10(EFW) = 1.326 - 0.00326(AC)(FL) + 0.0107(HC) + 0.0438(AC) + 0.158(FL)
 */
export const hadlockEFW_HC_AC_FL = (hc, ac, fl) => {
  if (!hc || !ac || !fl) return null;
  const hcCm = hc / 10;
  const acCm = ac / 10;
  const flCm = fl / 10;

  const log10EFW = 1.326 -
    (0.00326 * acCm * flCm) +
    (0.0107 * hcCm) +
    (0.0438 * acCm) +
    (0.158 * flCm);

  return Math.round(Math.pow(10, log10EFW));
};

/**
 * Hadlock V - Using BPD, HC, AC, and FL (Most comprehensive)
 * Log10(EFW) = 1.3596 + 0.0064(HC) + 0.0424(AC) + 0.174(FL) + 0.00061(BPD)(AC) - 0.00386(AC)(FL)
 */
export const hadlockEFW_Full = (bpd, hc, ac, fl) => {
  if (!bpd || !hc || !ac || !fl) return null;
  const bpdCm = bpd / 10;
  const hcCm = hc / 10;
  const acCm = ac / 10;
  const flCm = fl / 10;

  const log10EFW = 1.3596 +
    (0.0064 * hcCm) +
    (0.0424 * acCm) +
    (0.174 * flCm) +
    (0.00061 * bpdCm * acCm) -
    (0.00386 * acCm * flCm);

  return Math.round(Math.pow(10, log10EFW));
};

/**
 * Calculate EFW using best available measurements
 * Prioritizes formulas with more parameters for accuracy
 */
export const calculateEFW = (measurements) => {
  const { bpd, hc, ac, fl } = measurements;

  // Try most comprehensive first
  if (bpd && hc && ac && fl) {
    return { value: hadlockEFW_Full(bpd, hc, ac, fl), formula: 'Hadlock (BPD, HC, AC, FL)' };
  }
  if (bpd && ac && fl) {
    return { value: hadlockEFW_BPD_AC_FL(bpd, ac, fl), formula: 'Hadlock (BPD, AC, FL)' };
  }
  if (hc && ac && fl) {
    return { value: hadlockEFW_HC_AC_FL(hc, ac, fl), formula: 'Hadlock (HC, AC, FL)' };
  }
  if (ac && fl) {
    return { value: hadlockEFW_AC_FL(ac, fl), formula: 'Hadlock (AC, FL)' };
  }
  if (bpd && ac) {
    return { value: hadlockEFW_BPD_AC(bpd, ac), formula: 'Hadlock (BPD, AC)' };
  }

  return { value: null, formula: null };
};

// ============================================
// GESTATIONAL AGE FROM BIOMETRY
// ============================================

/**
 * GA from BPD (Hadlock)
 * GA = 9.54 + 1.482(BPD) + 0.1676(BPD²)
 */
export const gaFromBPD = (bpd) => {
  if (!bpd) return null;
  const bpdCm = bpd / 10;
  const weeks = 9.54 + (1.482 * bpdCm) + (0.1676 * Math.pow(bpdCm, 2));
  return Math.round(weeks * 10) / 10;
};

/**
 * GA from HC (Hadlock)
 * GA = 8.96 + 0.540(HC) + 0.0003(HC³)
 */
export const gaFromHC = (hc) => {
  if (!hc) return null;
  const hcCm = hc / 10;
  const weeks = 8.96 + (0.540 * hcCm) + (0.0003 * Math.pow(hcCm, 3));
  return Math.round(weeks * 10) / 10;
};

/**
 * GA from AC (Hadlock)
 * GA = 8.14 + 0.753(AC) + 0.0036(AC²)
 */
export const gaFromAC = (ac) => {
  if (!ac) return null;
  const acCm = ac / 10;
  const weeks = 8.14 + (0.753 * acCm) + (0.0036 * Math.pow(acCm, 2));
  return Math.round(weeks * 10) / 10;
};

/**
 * GA from FL (Hadlock)
 * GA = 10.35 + 2.460(FL) + 0.170(FL²)
 */
export const gaFromFL = (fl) => {
  if (!fl) return null;
  const flCm = fl / 10;
  const weeks = 10.35 + (2.460 * flCm) + (0.170 * Math.pow(flCm, 2));
  return Math.round(weeks * 10) / 10;
};

/**
 * GA from CRL (First trimester)
 * GA = 8.052 × √(CRL × 1.037) + 23.73 (days)
 * Robinson & Fleming formula
 */
export const gaFromCRL = (crl) => {
  if (!crl) return null;
  const days = (8.052 * Math.sqrt(crl * 1.037)) + 23.73;
  const weeks = days / 7;
  return Math.round(weeks * 10) / 10;
};

/**
 * Composite GA - average of available measurements
 */
export const calculateCompositeGA = (measurements) => {
  const { crl, bpd, hc, ac, fl } = measurements;
  const gaValues = [];

  // First trimester - CRL is most accurate
  if (crl && crl > 0) {
    const ga = gaFromCRL(crl);
    if (ga && ga <= 14) {
      return { weeks: ga, method: 'CRL', individual: { crl: ga } };
    }
  }

  // Second/Third trimester
  const individual = {};

  if (bpd) {
    const ga = gaFromBPD(bpd);
    if (ga) { gaValues.push(ga); individual.bpd = ga; }
  }
  if (hc) {
    const ga = gaFromHC(hc);
    if (ga) { gaValues.push(ga); individual.hc = ga; }
  }
  if (ac) {
    const ga = gaFromAC(ac);
    if (ga) { gaValues.push(ga); individual.ac = ga; }
  }
  if (fl) {
    const ga = gaFromFL(fl);
    if (ga) { gaValues.push(ga); individual.fl = ga; }
  }

  if (gaValues.length === 0) return null;

  const avgGA = gaValues.reduce((a, b) => a + b, 0) / gaValues.length;
  return {
    weeks: Math.round(avgGA * 10) / 10,
    method: 'Composite Average',
    individual
  };
};

/**
 * Format GA as weeks+days
 */
export const formatGA = (weeks) => {
  if (!weeks) return '-';
  const w = Math.floor(weeks);
  const d = Math.round((weeks - w) * 7);
  return `${w}w ${d}d`;
};

// ============================================
// ESTIMATED DUE DATE (EDD)
// ============================================

/**
 * Calculate EDD from LMP
 */
const isDateOnlyString = (value) =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

const parseDateInput = (value) => {
  if (value instanceof Date) return new Date(value);
  if (isDateOnlyString(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(value);
};

export const eddFromLMP = (lmpDate) => {
  if (!lmpDate) return null;
  const lmp = parseDateInput(lmpDate);
  const edd = new Date(lmp);
  edd.setDate(edd.getDate() + 280); // 40 weeks
  return edd;
};

/**
 * Calculate EDD from current GA
 */
export const eddFromGA = (gaWeeks, examDate = new Date()) => {
  if (!gaWeeks) return null;
  const daysRemaining = (40 - gaWeeks) * 7;
  const edd = parseDateInput(examDate);
  edd.setDate(edd.getDate() + daysRemaining);
  return edd;
};

/**
 * Calculate current GA from LMP
 */
export const gaFromLMP = (lmpDate, currentDate = new Date()) => {
  if (!lmpDate) return null;
  const lmp = parseDateInput(lmpDate);
  const diffTime = parseDateInput(currentDate) - lmp;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return Math.round((diffDays / 7) * 10) / 10;
};

// ============================================
// GROWTH PERCENTILES (Hadlock)
// ============================================

/**
 * Hadlock growth percentile data
 * Mean and SD for EFW at each gestational age
 */
const hadlockGrowthData = {
  // GA: [mean_grams, sd_grams]
  14: [93, 12], 15: [117, 15], 16: [146, 19], 17: [181, 24],
  18: [223, 29], 19: [273, 35], 20: [331, 43], 21: [399, 52],
  22: [478, 62], 23: [568, 74], 24: [670, 87], 25: [785, 102],
  26: [913, 119], 27: [1055, 137], 28: [1210, 157], 29: [1379, 179],
  30: [1559, 203], 31: [1751, 228], 32: [1953, 254], 33: [2162, 281],
  34: [2377, 309], 35: [2595, 338], 36: [2813, 367], 37: [3028, 396],
  38: [3236, 424], 39: [3435, 451], 40: [3619, 476], 41: [3787, 499],
  42: [3936, 519]
};

/**
 * Calculate EFW percentile
 */
export const calculateEFWPercentile = (efwGrams, gaWeeks) => {
  if (!efwGrams || !gaWeeks) return null;

  const roundedGA = Math.round(gaWeeks);
  if (roundedGA < 14 || roundedGA > 42) return null;

  const [mean, sd] = hadlockGrowthData[roundedGA];
  const zScore = (efwGrams - mean) / sd;

  // Convert z-score to percentile using standard normal distribution
  const percentile = standardNormalCDF(zScore) * 100;

  return {
    percentile: Math.round(percentile),
    zScore: Math.round(zScore * 100) / 100,
    mean,
    sd
  };
};

/**
 * Standard normal cumulative distribution function
 */
const standardNormalCDF = (z) => {
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  const sign = z < 0 ? -1 : 1;
  z = Math.abs(z) / Math.sqrt(2);

  const t = 1.0 / (1.0 + p * z);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-z * z);

  return 0.5 * (1.0 + sign * y);
};

/**
 * Get expected EFW for percentile at GA
 */
export const getExpectedEFW = (gaWeeks, percentile = 50) => {
  const roundedGA = Math.round(gaWeeks);
  if (roundedGA < 14 || roundedGA > 42) return null;

  const [mean, sd] = hadlockGrowthData[roundedGA];
  const zScore = inverseStandardNormal(percentile / 100);

  return Math.round(mean + (zScore * sd));
};

/**
 * Approximate inverse standard normal
 */
const inverseStandardNormal = (p) => {
  if (p <= 0) return -Infinity;
  if (p >= 1) return Infinity;
  if (p === 0.5) return 0;

  // Rational approximation
  const a = [
    -3.969683028665376e+01, 2.209460984245205e+02,
    -2.759285104469687e+02, 1.383577518672690e+02,
    -3.066479806614716e+01, 2.506628277459239e+00
  ];
  const b = [
    -5.447609879822406e+01, 1.615858368580409e+02,
    -1.556989798598866e+02, 6.680131188771972e+01,
    -1.328068155288572e+01
  ];
  const c = [
    -7.784894002430293e-03, -3.223964580411365e-01,
    -2.400758277161838e+00, -2.549732539343734e+00,
    4.374664141464968e+00, 2.938163982698783e+00
  ];
  const d = [
    7.784695709041462e-03, 3.224671290700398e-01,
    2.445134137142996e+00, 3.754408661907416e+00
  ];

  const pLow = 0.02425;
  const pHigh = 1 - pLow;
  let q, r;

  if (p < pLow) {
    q = Math.sqrt(-2 * Math.log(p));
    return (((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) /
           ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
  } else if (p <= pHigh) {
    q = p - 0.5;
    r = q * q;
    return (((((a[0]*r+a[1])*r+a[2])*r+a[3])*r+a[4])*r+a[5])*q /
           (((((b[0]*r+b[1])*r+b[2])*r+b[3])*r+b[4])*r+1);
  } else {
    q = Math.sqrt(-2 * Math.log(1 - p));
    return -(((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) /
            ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
  }
};

/**
 * Generate growth curve data for charting
 */
export const generateGrowthCurve = (startGA = 14, endGA = 42) => {
  const curve = {
    weeks: [],
    p3: [], p10: [], p50: [], p90: [], p97: []
  };

  for (let ga = startGA; ga <= endGA; ga++) {
    curve.weeks.push(ga);
    curve.p3.push(getExpectedEFW(ga, 3));
    curve.p10.push(getExpectedEFW(ga, 10));
    curve.p50.push(getExpectedEFW(ga, 50));
    curve.p90.push(getExpectedEFW(ga, 90));
    curve.p97.push(getExpectedEFW(ga, 97));
  }

  return curve;
};

// ============================================
// BIOMETRY RATIOS
// ============================================

/**
 * Head Circumference / Abdominal Circumference ratio
 * Normal: ~1.0 at term, higher early pregnancy
 */
export const calculateHC_AC_Ratio = (hc, ac) => {
  if (!hc || !ac) return null;
  return Math.round((hc / ac) * 100) / 100;
};

/**
 * FL / AC ratio - screens for skeletal dysplasia
 * Normal: 0.20 - 0.24
 */
export const calculateFL_AC_Ratio = (fl, ac) => {
  if (!fl || !ac) return null;
  return Math.round((fl / ac) * 100) / 100;
};

/**
 * FL / BPD ratio
 * Normal: 0.71 - 0.87
 */
export const calculateFL_BPD_Ratio = (fl, bpd) => {
  if (!fl || !bpd) return null;
  return Math.round((fl / bpd) * 100) / 100;
};

/**
 * Cephalic Index (CI) = BPD / OFD × 100
 * Normal: 70 - 86
 */
export const calculateCephalicIndex = (bpd, ofd) => {
  if (!bpd || !ofd) return null;
  return Math.round((bpd / ofd) * 100);
};

// ============================================
// AMNIOTIC FLUID
// ============================================

/**
 * Amniotic Fluid Index interpretation
 * Sum of deepest vertical pocket in each quadrant
 */
export const interpretAFI = (afi) => {
  if (!afi && afi !== 0) return null;

  if (afi < 5) return { status: 'Oligohydramnios', severity: 'abnormal' };
  if (afi < 8) return { status: 'Low-normal', severity: 'borderline' };
  if (afi <= 24) return { status: 'Normal', severity: 'normal' };
  if (afi <= 25) return { status: 'High-normal', severity: 'borderline' };
  return { status: 'Polyhydramnios', severity: 'abnormal' };
};

/**
 * Single Deepest Pocket interpretation
 */
export const interpretSDP = (sdp) => {
  if (!sdp && sdp !== 0) return null;

  if (sdp < 2) return { status: 'Oligohydramnios', severity: 'abnormal' };
  if (sdp <= 8) return { status: 'Normal', severity: 'normal' };
  return { status: 'Polyhydramnios', severity: 'abnormal' };
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Convert weight between units
 */
export const convertWeight = (value, from, to) => {
  const grams = from === 'g' ? value :
                from === 'kg' ? value * 1000 :
                from === 'lb' ? value * 453.592 : value;

  if (to === 'g') return Math.round(grams);
  if (to === 'kg') return Math.round(grams / 1000 * 100) / 100;
  if (to === 'lb') return Math.round(grams / 453.592 * 100) / 100;
  return grams;
};

/**
 * Format weight with appropriate unit
 */
export const formatWeight = (grams) => {
  if (!grams) return '-';
  if (grams >= 1000) {
    return `${(grams / 1000).toFixed(2)} kg (${Math.round(grams)} g)`;
  }
  return `${Math.round(grams)} g`;
};

export default {
  // EFW
  calculateEFW,
  hadlockEFW_Full,
  hadlockEFW_BPD_AC_FL,
  hadlockEFW_HC_AC_FL,
  hadlockEFW_AC_FL,
  hadlockEFW_BPD_AC,

  // GA
  calculateCompositeGA,
  gaFromCRL,
  gaFromBPD,
  gaFromHC,
  gaFromAC,
  gaFromFL,
  gaFromLMP,
  formatGA,

  // EDD
  eddFromLMP,
  eddFromGA,

  // Percentiles
  calculateEFWPercentile,
  getExpectedEFW,
  generateGrowthCurve,

  // Ratios
  calculateHC_AC_Ratio,
  calculateFL_AC_Ratio,
  calculateFL_BPD_Ratio,
  calculateCephalicIndex,

  // Amniotic Fluid
  interpretAFI,
  interpretSDP,

  // Utilities
  convertWeight,
  formatWeight,
};
